import React, { useState } from 'react';
import LoginContent from './LoginContent';
import SignupContent from './SignupContent';  
import PasswordContent from './PasswordContent';
import EmailContent from './EmailContent';
import VerificationContent from './VerificationContent';
import CloseButton from '@/components/ui/CloseButton';
import { useAuthModal } from './AuthModalContext';
import styles from './AuthModal.module.css';

// Auth modal modes
export type AuthMode = 'login' | 'signup' | 'signupEmail' | 'password' | 'email' | 'verification';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: AuthMode;
  redirectAfterAuth?: string;
  contextMessage?: string;
  preventClose?: boolean; // Prevent closing modal (for verification flow)
  resetPreventClose?: () => void; // Reset prevent close (for verification completion)
}


// Fixed image panel component  
const AuthImage = () => (
  <div className={styles.imagePanel}>
    <img 
      src="/images/Tios/Earning-Tio.webp" 
      alt="Tio Assistant"
      className={styles.authImage}
    />
  </div>
);

const AuthModal: React.FC<AuthModalProps> = ({ 
  isOpen, 
  onClose, 
  initialMode = 'login',
  redirectAfterAuth,
  contextMessage,
  preventClose = false,
  resetPreventClose
}) => {
  // Use initialMode directly instead of local state to avoid conflicts
  const currentMode = initialMode;

  const handleClose = () => {
    // Prevent closing if in verification mode and preventClose is true
    if (preventClose && currentMode === 'verification') {
      return;
    }
    onClose();
  };

  const { switchMode: contextSwitchMode } = useAuthModal();
  
  const switchMode = (mode: AuthMode) => {
    contextSwitchMode(mode);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className={styles.backdrop} onClick={handleClose} />
      
      {/* Modal */}
      <div className={styles.authModal} role="dialog" aria-modal="true">
        {/* Only show close button if not in prevented close mode */}
        {!(preventClose && currentMode === 'verification') && (
          <CloseButton onClick={handleClose} />
        )}
        
        <div className={styles.modalContent}>
          {/* Content Panel */}
          <div className={styles.contentPanel}>
            {currentMode === 'login' && (
              <LoginContent 
                onSwitchMode={switchMode}
                onSuccess={handleClose}
                contextMessage={contextMessage}
              />
            )}
            {currentMode === 'signup' && (
              <SignupContent 
                onSwitchMode={switchMode}
                onSuccess={handleClose}
                redirectAfterAuth={redirectAfterAuth}
              />
            )}
            {currentMode === 'signupEmail' && (
              <SignupContent 
                onSwitchMode={switchMode}
                onSuccess={handleClose}
                redirectAfterAuth={redirectAfterAuth}
              />
            )}
            {currentMode === 'password' && (
              <PasswordContent 
                onSwitchMode={switchMode}
                onSuccess={handleClose}
              />
            )}
            {currentMode === 'email' && (
              <EmailContent 
                onSwitchMode={switchMode}
                onSuccess={handleClose}
              />
            )}
            {currentMode === 'verification' && (
              <VerificationContent 
                onSwitchMode={switchMode}
                onSuccess={handleClose}
                resetPreventClose={resetPreventClose}
              />
            )}
          </div>

          {/* Fixed Image Panel */}
          <AuthImage />
        </div>
      </div>
    </>
  );
};

export default AuthModal;